import type { CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import type { ProfileOut } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import type { FilterOption } from '@/components/common/SelectFilter';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type ProfileSelectFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
};

/**
 * `profile_id[eq]` column filter with server-side name autocomplete —
 * type a name fragment, pick a profile, filter by its id. Profile names are
 * nullable, so an unnamed profile falls back to its id.
 */
export const ProfileSelectFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
}: ProfileSelectFilterProps) => {
  const current = getFilterValue<number>(filters, field, 'eq');

  const { selectProps, query, defaultValueQuery } = useSelect<ProfileOut>({
    resource: 'account.profiles',
    optionLabel: 'name',
    optionValue: 'id',
    // Hydrates the option label when the filter is already set (e.g. from URL).
    defaultValue: current,
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  const byId = new Map<number, ProfileOut>(
    [...(defaultValueQuery.data?.data ?? []), ...(query.data?.data ?? [])].map(
      (profile) => [profile.id, profile]
    )
  );

  const options: FilterOption<number>[] = [...byId.values()].map((profile) => ({
    value: profile.id,
    label: profile.name ?? `#${profile.id}`,
  }));

  return (
    <SelectFilter<number>
      field={field}
      options={options}
      filters={filters}
      confirm={confirm}
      clearFilters={clearFilters}
      setFilters={setFilters}
      placeholder={placeholder}
      minWidth={260}
      selectProps={{
        showSearch: true,
        filterOption: false,
        onSearch: selectProps.onSearch,
      }}
    />
  );
};
